/**
 * The name of ONE press (CLAUDE.md, invariant 5).
 *
 * # Why a name and not a function called at submit time
 *
 * A name minted inside the call is a new name on every attempt, and a new name is a new
 * ACT to the service: the retry a person makes after a lost answer — the spinner that
 * never stopped, the tab reloaded, the button pressed twice — then arrives as a second
 * stocktake, a second pallet note, a second rescale of a cellar. The whole idempotency
 * mechanism is the id being the SAME across the attempts of one press, so it has to
 * outlive the call.
 *
 * # Where the name is KEPT is `libs/operation.ts`, and this file only mints
 *
 * This module used to carry a `useOperationId(deps)` composable as well: a ref minted on
 * open and re-minted when the content changed, which each screen then had to spend by
 * hand on success. Eleven screens did that, in five slightly different ways, and the
 * difference was where the fault was: the chessboard spent its name on SUCCESS only, so
 * an operation the service had CLOSED for good (StayChangeSuperseded, 134) left the name
 * behind and every later press of the same drag was refused under a name that could
 * never be accepted again.
 *
 * Keeping a name and deciding when to drop it are one decision, so they are now in one
 * place — `useOperation()` — which owns the Map and reads the refusal's own verdict
 * (`retryable` / `operation_closed`) rather than letting each screen judge a code.
 *
 * What is left here is the minting, because that genuinely is used in two ways: by
 * `useOperation()` for a press that must survive a retry, and directly by the handful of
 * library calls that need a name only to satisfy a request's shape check —
 * `libs/stockTransfer.ts`, `libs/stockCount.ts` — where the act is a button on a record
 * whose state the service reads anyway. Each of those says so where it does it.
 */

/** A fresh name. `randomUUID` where the browser has it; a unique-enough string where not. */
export const mintOperationId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID()
    }

    return `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
